/**
 * Environment configuration types for multi-environment Cognito authentication
 */

/**
 * Supported environment identifiers
 */
export type EnvironmentName = 'local' | 'dev' | 'prod';

/**
 * Available API targets for local development
 * Allows developers to choose which backend to connect to at runtime
 */
export type ApiTarget = 'local' | 'dev' | 'prod';

/**
 * Developer mode configuration stored in localStorage
 * Enables runtime switching of API targets without restart
 */
export interface DevModeConfig {
  /** Selected API target (local or dev) when using dev Cognito */
  apiTarget: ApiTarget;

  /** Timestamp of last configuration change (for debugging) */
  lastModified: string; // ISO 8601 timestamp

  /** Version for future migration support */
  version: 1;
}

/**
 * Information for displaying current environment state
 * Used by EnvIndicator to show combined auth + API info
 */
export interface EnvironmentDisplayInfo {
  /** Auth environment name (local, dev, prod) */
  authEnvironment: EnvironmentName;

  /** Human-readable auth environment display name */
  authDisplayName: string;

  /** Target API environment (may differ from auth environment) */
  apiTarget: ApiTarget;

  /** Whether using mock authentication */
  isMock: boolean;

  /** Combined display text (e.g., "Dev Auth + Local API") */
  displayText: string;

  /** Severity level for styling */
  severity: 'info' | 'warning' | 'danger';
}

/**
 * Context value for dev mode configuration management
 * Provides runtime configuration state and update methods
 */
export interface DevModeContextValue {
  /** Current configuration */
  config: DevModeConfig;

  /** Whether dev mode settings are applicable (not in deployed/prod environment) */
  isDevModeAvailable: boolean;

  /** Update the API target */
  setApiTarget: (target: ApiTarget) => void;

  /** Reset to default configuration */
  resetConfig: () => void;

  /** Get the effective API URL based on current configuration */
  getEffectiveApiUrl: () => string;
}

/**
 * Complete configuration for a specific deployment environment
 */
export interface EnvironmentConfig {
  /** Unique environment identifier */
  name: EnvironmentName;

  /** Human-readable environment name (displayed in UI) */
  displayName: string;

  /** AWS Cognito issuer URL (OIDC authority) */
  cognitoAuthority: string;

  /** OAuth 2.0 client ID for this environment */
  cognitoClientId: string;

  /** Cognito hosted UI domain (for login/logout redirects) */
  cognitoDomain: string;

  /** OAuth redirect URI after successful authentication */
  redirectUri: string;

  /** Redirect URI after logout */
  postLogoutRedirectUri: string;

  /** Space-separated OAuth scopes */
  scope: string;

  /** Backend API base URL for this environment */
  apiUrl: string;

  /** Frontend application URL (for Cognito callback configuration) */
  appUri: string;

  /** Whether this is a mock environment (no real Cognito) */
  isMock: boolean;
}

/**
 * OIDC client configuration (compatible with oidc-client-ts)
 */
export interface OidcConfig {
  authority: string;
  client_id: string;
  redirect_uri: string;
  post_logout_redirect_uri: string;
  response_type: 'code';
  scope: string;
  loadUserInfo: boolean;
  automaticSilentRenew: boolean;
  accessTokenExpiringNotificationTimeInSeconds?: number;
}

/**
 * Configuration validation result
 */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Environment variable keys
 */
export const ENV_KEYS = {
  ENVIRONMENT: 'VITE_ENVIRONMENT',

  // Development environment
  DEV_AUTHORITY: 'VITE_DEV_COGNITO_AUTHORITY',
  DEV_CLIENT_ID: 'VITE_DEV_COGNITO_CLIENT_ID',
  DEV_DOMAIN: 'VITE_DEV_COGNITO_DOMAIN',
  DEV_API_URL: 'VITE_DEV_API_URL',

  // Production environment
  PROD_AUTHORITY: 'VITE_PROD_COGNITO_AUTHORITY',
  PROD_CLIENT_ID: 'VITE_PROD_COGNITO_CLIENT_ID',
  PROD_DOMAIN: 'VITE_PROD_COGNITO_DOMAIN',
  PROD_API_URL: 'VITE_PROD_API_URL',

  // Mock environment
  MOCK_USER_EMAIL: 'VITE_MOCK_USER_EMAIL',
  MOCK_USER_NAME: 'VITE_MOCK_USER_NAME',

  // Deployed environment (terraform-injected)
  AUTHORITY: 'VITE_AUTHORITY',
  OIDC_CLIENT_ID: 'VITE_OIDC_CLIENT_ID',
  OIDC_REDIRECT_URI: 'VITE_OIDC_REDIRECT_URI',
  OIDC_POST_LOGOUT_REDIRECT_URI: 'VITE_OIDC_POST_LOGOUT_REDIRECT_URI',
  OIDC_SCOPE: 'VITE_OIDC_SCOPE',
  API_URL: 'VITE_API_URL',
  COGNITO_DOMAIN: 'VITE_COGNITO_DOMAIN',
} as const;
